/**
 * _shared.tsx
 * ─────────────────────────────────────────────────────────────────
 * Helpers UI partagés entre les pages-guides longs-formes des aides
 * particuliers (MaPrimeRénov', Prime énergie, éco-PTZ, TVA 5,5 %,
 * Coup de pouce).
 *
 * Ces helpers garantissent la cohérence visuelle entre les 5 guides :
 *  - `SectionHeader` : eyebrow + titre + sous-titre
 *  - `FeatureCallout` : carte mini-feature (icône + titre + texte)
 *  - `Breadcrumb` : fil d'Ariane standard "Particuliers / Aides / X"
 *  - Constantes SITE_URL et CTA_HREF
 */

import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';

export const SITE_URL = 'https://solutionecofrance.fr';
export const CTA_HREF = '/particuliers/calculer-mes-aides';

// ════════════════════════════════════════════════════════════════
// SectionHeader
// ════════════════════════════════════════════════════════════════

export interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  align?: 'left' | 'center';
}

export function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'left',
}: SectionHeaderProps) {
  return (
    <header
      className={cn(
        'mb-10',
        align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl',
      )}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
        {eyebrow}
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold leading-tight tracking-tight text-ink md:text-[2rem]">
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-3 text-[15px] leading-relaxed text-stone-600 md:text-base">
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}

// ════════════════════════════════════════════════════════════════
// FeatureCallout
// ════════════════════════════════════════════════════════════════

export interface FeatureCalloutProps {
  iconName: string;
  title: string;
  text: string;
}

export function FeatureCallout({ iconName, title, text }: FeatureCalloutProps) {
  const Icon = getIcon(iconName);
  return (
    <div className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
        <Icon className="h-4 w-4" strokeWidth={1.7} />
      </span>
      <p className="mt-3 text-[14px] font-semibold text-ink">{title}</p>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-stone-600">{text}</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// AideBreadcrumb
// ════════════════════════════════════════════════════════════════

export function AideBreadcrumb({ current }: { current: string }) {
  return (
    <nav aria-label="Fil d&rsquo;Ariane" className="mb-6 text-[12px] text-stone-500">
      <a href="/particuliers" className="hover:text-brand-700">
        Particuliers
      </a>
      <span className="mx-1.5 text-stone-300">/</span>
      <a href="/particuliers/aides" className="hover:text-brand-700">
        Aides
      </a>
      <span className="mx-1.5 text-stone-300">/</span>
      <span className="font-medium text-stone-700">{current}</span>
    </nav>
  );
}
